import { storeConfig } from '@/shared/storeConfig';

export const useSmarticoUserInfos = defineStore(
  'smarticoUserInfos',
  () => {
    const { debugLog } = useSmarticoConfigs();

    const isLoadingUserInfos = ref<boolean>(false);
    const userInfosProfile = ref<TUserProfile>();
    const currentUserLevelData = ref<TLevel>();
    const nextUserLevel = ref<TLevel>();
    const userLevelNumber = ref<number>(0);
    const progressToAnotherLevel = ref<number>(0);
    const userInfosDateUpdate = ref<string>();
    const loadUserInfosPromise = ref<Promise<void> | null>(null);

    const currentUserLevelName = computed(() => {
      if (
        !currentUserLevelData.value?.name &&
        !userInfosProfile.value?.ach_level_current
      ) {
        return '---';
      }

      const currentLevel = currentUserLevelData.value?.name
        ? currentUserLevelData.value?.name.split('-')
        : userInfosProfile.value?.ach_level_current
          ? userInfosProfile.value?.ach_level_current.split('-')
          : '---';

      return currentLevel && currentLevel[0] ? currentLevel[0] : '---';
    });

    const currentUserLevelImage = computed(() => {
      if (currentUserLevelData.value?.image) {
        return currentUserLevelData.value?.image;
      }

      if (userInfosProfile.value?.avatar_url) {
        return userInfosProfile.value?.avatar_url;
      }

      return getAssetsUrlForCDN(`/assets/gamification/level.png`);
    });

    const currentUserOnlyLevelImage = computed(() => {
      if (currentUserLevelData.value?.image) {
        return currentUserLevelData.value?.image;
      }
    });

    const currentUserProfileAvatar = computed(() => {
      return userInfosProfile.value?.avatar_url
        ? userInfosProfile.value?.avatar_url
        : getAssetsUrlForCDN(`/assets/gamification/user.png`);
    });

    function mergeUserInfosProfile(profile?: Partial<TUserProfile>) {
      if (!profile) {
        return;
      }

      userInfosProfile.value = {
        ...userInfosProfile.value,
        ...profile,
      } as TUserProfile;
    }

    async function getSmarticoUserProfile() {
      const profile = window._smartico?.api?.getUserProfile?.();

      if (!profile) {
        return undefined;
      }

      return await Promise.resolve(profile);
    }

    function loadUserInfosProfile(props?: Partial<TUserProfile>) {
      mergeUserInfosProfile(props);

      if (loadUserInfosPromise.value) {
        return loadUserInfosPromise.value;
      }

      loadUserInfosPromise.value = loadUserInfosProfileData().finally(() => {
        loadUserInfosPromise.value = null;
      });

      return loadUserInfosPromise.value;
    }

    async function loadUserInfosProfileData() {
      try {
        isLoadingUserInfos.value = true;

        const profile = await getSmarticoUserProfile();

        mergeUserInfosProfile(profile);

        debugLog('userInfosProfile:', userInfosProfile.value);

        await loadUserLevelData();

        setTimeout(() => {
          isLoadingUserInfos.value = false;

          const now = new Date();

          userInfosDateUpdate.value = now.toString();
        }, 300);
      } catch (err: any) {
        debugLog(`⭕️ ~ getUserProfile ~ ${err.toString()}`);
        isLoadingUserInfos.value = false;
      }
    }

    async function loadUserLevelData() {
      try {
        const { allLevels } = storeToRefs(useSmarticoLevels());
        const { loadLevels } = useSmarticoLevels();

        if (!userInfosProfile.value) {
          return;
        }

        if (!allLevels.value) {
          await loadLevels();
        }

        if (!allLevels.value?.length) {
          await loadCurrentLevelData();

          return;
        }

        const indexLevel = allLevels.value.findIndex(
          (e: any) =>
            e && e.id && e.id === userInfosProfile?.value?.ach_level_current_id,
        );

        if (indexLevel < 0) {
          await loadCurrentLevelData();

          return;
        }

        const currentLevelData = allLevels.value[indexLevel];

        if (!currentLevelData) {
          await loadCurrentLevelData();

          return;
        }

        userLevelNumber.value = indexLevel + 1;
        currentUserLevelData.value = currentLevelData;
        nextUserLevel.value = allLevels.value[indexLevel + 1];

        const userPoints = userInfosProfile.value.ach_points_ever;
        const currentLevelPoints = currentLevelData.required_points;
        const nextLevelPoints = nextUserLevel.value?.required_points;

        if (nextLevelPoints !== undefined) {
          const progressInLevel =
            ((userPoints - currentLevelPoints) * 100) /
            (nextLevelPoints - currentLevelPoints);

          progressToAnotherLevel.value = Math.max(
            0,
            Math.min(progressInLevel, 100),
          );
        } else {
          // Usuário está no nível máximo
          progressToAnotherLevel.value = 100;
        }
      } catch (err: any) {
        debugLog(`⭕️ ~ loadUserLevelData ~ ${err.toString()}`);
      }
    }

    async function loadCurrentLevelData() {
      try {
        const currentLevel = await window._smartico?.api?.getCurrentLevel?.();

        if (!currentLevel) {
          return;
        }

        currentUserLevelData.value = currentLevel;
        userLevelNumber.value = currentLevel.ordinal_position ?? 0;
        progressToAnotherLevel.value = Number.isFinite(currentLevel.progress)
          ? Math.max(0, Math.min(currentLevel.progress, 100))
          : 0;
      } catch (err: any) {
        debugLog(`⭕️ ~ getCurrentLevel ~ ${err.toString()}`);
      }
    }

    function buildLevelPayload(level: TLevel, progress: number): LevelPayload {
      if (!level) {
        throw new Error('Level data is required');
      }

      const safeProgress = Number.isFinite(progress)
        ? Math.max(0, Math.min(100, progress))
        : 0;

      const safeOrdinalPosition = Number.isFinite(userLevelNumber.value)
        ? Math.max(0, userLevelNumber.value)
        : 0;

      const safeRequiredPoints = Number.isFinite(level.required_points)
        ? Math.max(0, level.required_points)
        : 0;

      const safeVisibilityPoints = Number.isFinite(level.visibility_points)
        ? Math.max(0, level.visibility_points)
        : 0;

      return {
        custom_data: {},
        description: level.description || '',
        image: level.image || '',
        name: level.name || '',
        ordinal_position: safeOrdinalPosition,
        progress: safeProgress,
        required_level_counter_1:
          level.required_level_counter_1 != null &&
          Number.isFinite(level.required_level_counter_1)
            ? level.required_level_counter_1
            : null,
        required_level_counter_2:
          level.required_level_counter_2 != null &&
          Number.isFinite(level.required_level_counter_2)
            ? level.required_level_counter_2
            : null,
        required_points: safeRequiredPoints,
        visibility_points: safeVisibilityPoints,
      };
    }

    async function sendUserLevelData(retryCount: number = 0) {
      const MAX_RETRIES = 3;
      const RETRY_DELAY = 2000;

      try {
        if (
          !currentUserLevelData.value ||
          !nextUserLevel.value ||
          !userInfosProfile.value
        ) {
          debugLog(
            '⚠️ ~ sendUserLevelData ~ Level data not available in sendUserLevelData',
          );

          return;
        }

        const { userId } = useUserData();
        const { ApiService } = useApiService();

        if (!userId.value) {
          debugLog('⚠️ ~ sendUserLevelData ~ User ID not available');

          return;
        }

        const userPoints = userInfosProfile.value?.ach_points_ever ?? 0;
        const nextLevelProgress =
          nextUserLevel.value.required_points > 0
            ? Math.min(
                (userPoints * 100) / nextUserLevel.value.required_points,
                100,
              )
            : 100;

        const payload: UserGamificationPayload = {
          levels_payload: {
            current_level: buildLevelPayload(
              currentUserLevelData.value,
              progressToAnotherLevel.value,
            ),
            next_level: buildLevelPayload(
              nextUserLevel.value,
              nextLevelProgress,
            ),
          },
        };

        debugLog('sendUserLevelData payload:', payload);

        const response = await ApiService.patch(
          `/bff/users/gamification`,
          payload,
        );

        debugLog('✅ ~ sendUserLevelData ~ Success:', response);
      } catch (err: any) {
        const errorStatus = err?.status || err?.response?.status;
        const errorMessage =
          err?.data?.message || err?.message || 'Unknown error';

        debugLog(
          `⭕️ ~ sendUserLevelData ~ Error [${errorStatus}]:`,
          errorMessage,
        );

        const isNetworkError = !errorStatus || errorStatus >= 500;
        const isRateLimitError = errorStatus === 429;
        const isClientError = errorStatus >= 400 && errorStatus < 500;

        if ((isNetworkError || isRateLimitError) && retryCount < MAX_RETRIES) {
          const delay = RETRY_DELAY * Math.pow(2, retryCount);

          debugLog(
            `🔄 ~ sendUserLevelData ~ Retrying in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`,
          );

          await new Promise((resolve) => setTimeout(resolve, delay));

          return sendUserLevelData(retryCount + 1);
        }

        if (isClientError && errorStatus !== 429) {
          debugLog(
            `🚫 ~ sendUserLevelData ~ Client error (${errorStatus}), not retrying:`,
            err?.data || err,
          );
        }

        if (retryCount >= MAX_RETRIES) {
          debugLog(
            `❌ ~ sendUserLevelData ~ Max retries reached. Last error:`,
            errorMessage,
          );
        }
      }
    }

    function openChangeAvatar() {
      try {
        window._smartico.dp(
          `dp:gf_change_avatar&standalone=true&hide_main=true`,
        );
      } catch (err: any) {
        debugLog(`⭕️ ~ dp:gf_change_avatar ~ ${err.toString()}`);
      }
    }

    function openChangeNickname() {
      try {
        window._smartico.dp(
          `dp:gf_change_nickname&standalone=true&hide_main=true`,
        );

        // O apelido é editado DENTRO do widget da Smartico: nada avisa o app
        // quando ele muda, e sem isso o nome antigo fica na tela até o próximo
        // reload. Recarregamos o perfil quando o widget devolve o foco pra
        // página — uma vez só, e sem `await` porque quem chama é um @click.
        if (typeof window !== 'undefined') {
          window.addEventListener('focus', () => loadUserInfosProfile(), {
            once: true,
          });
        }
      } catch (err: any) {
        debugLog(`⭕️ ~ dp:gf_change_nickname ~ ${err.toString()}`);
      }
    }

    watch(
      [currentUserLevelData, nextUserLevel, userLevelNumber],
      async (
        [newCurrentLevel, newNextLevel, newLevelNumber],
        [oldCurrentLevel, oldNextLevel, oldLevelNumber],
      ) => {
        const hasLevelDataChanged =
          newCurrentLevel?.id !== oldCurrentLevel?.id ||
          newNextLevel?.id !== oldNextLevel?.id ||
          newLevelNumber !== oldLevelNumber;

        if (hasLevelDataChanged && newCurrentLevel && newNextLevel) {
          debugLog('User level changed, sending data to backoffice');
          await sendUserLevelData();
        }
      },
    );

    function $reset() {
      isLoadingUserInfos.value = false;
      loadUserInfosPromise.value = null;
      userInfosProfile.value = undefined;
      currentUserLevelData.value = undefined;
      nextUserLevel.value = undefined;
      userLevelNumber.value = 0;
      progressToAnotherLevel.value = 0;
      userInfosDateUpdate.value = undefined;
    }

    return {
      isLoadingUserInfos,
      userInfosProfile,
      currentUserProfileAvatar,
      userLevelNumber,
      nextUserLevel,
      currentUserLevelData,
      currentUserLevelName,
      currentUserLevelImage,
      currentUserOnlyLevelImage,
      progressToAnotherLevel,
      loadUserInfosProfile,
      openChangeAvatar,
      openChangeNickname,
      userInfosDateUpdate,
      $reset,
    };
  },

  storeConfig,
);
